import type { ECharts } from "echarts/core";
import { type CSSProperties, type JSX, type ReactNode, useEffect, useState } from "react";
import { layoutTextMeasurer } from "@/utils/text";
import type { TimeEvent } from "../../band-data/types";
import { formatAxisPointerTime } from "../../format";
import {
    CHART_FONT_FAMILY,
    CROSSHAIR_LABEL_BACKGROUND,
    CROSSHAIR_LABEL_BORDER_PX,
    CROSSHAIR_LABEL_FONT,
    CROSSHAIR_LABEL_FONT_SIZE_PX,
    CROSSHAIR_LABEL_FONT_WEIGHT,
    CROSSHAIR_LABEL_HEIGHT_PX,
    CROSSHAIR_LABEL_LINE_HEIGHT_PX,
    CROSSHAIR_LABEL_PADDING_BOTTOM_PX,
    CROSSHAIR_LABEL_PADDING_TOP_PX,
    CROSSHAIR_LABEL_PADDING_X_PX,
    CROSSHAIR_LABEL_RADIUS_PX,
    CROSSHAIR_LABEL_TEXT_COLOR,
    X_AXIS_POINTER_LABEL_MARGIN_PX,
} from "../layout/plot-geometry";
import type { PlotLayout } from "../layout/plot-layout";
import { BAND_ABOVE_RESERVE_PX, BAND_STRIP_EXTRA_PX, PILL_ROW_GAP_PX } from "./band-geometry";
import { formatSpan } from "./format-span";

const LABEL_OPACITY = 0.6;
const SEPARATOR = " · ";

const PILL_STYLE: CSSProperties = {
    height: CROSSHAIR_LABEL_HEIGHT_PX,
    paddingTop: CROSSHAIR_LABEL_PADDING_TOP_PX,
    paddingBottom: CROSSHAIR_LABEL_PADDING_BOTTOM_PX,
    paddingInline: CROSSHAIR_LABEL_PADDING_X_PX,
    border: `${CROSSHAIR_LABEL_BORDER_PX}px solid ${CROSSHAIR_LABEL_BACKGROUND}`,
    borderRadius: CROSSHAIR_LABEL_RADIUS_PX,
    backgroundColor: CROSSHAIR_LABEL_BACKGROUND,
    color: CROSSHAIR_LABEL_TEXT_COLOR,
    fontFamily: CHART_FONT_FAMILY,
    fontSize: CROSSHAIR_LABEL_FONT_SIZE_PX,
    fontWeight: CROSSHAIR_LABEL_FONT_WEIGHT,
    lineHeight: `${CROSSHAIR_LABEL_LINE_HEIGHT_PX}px`,
    // The page's tracking would make the text wider than the canvas measured it.
    letterSpacing: 0,
};

/** Centred on x, kept inside [minX, maxX]. */
function clampPillLeft(centerX: number, widthPx: number, minX: number, maxX: number): number {
    return Math.max(minX, Math.min(centerX - widthPx / 2, maxX - widthPx));
}

function pillWidth(text: string): number {
    return (
        layoutTextMeasurer(text, CROSSHAIR_LABEL_FONT) + 2 * (CROSSHAIR_LABEL_PADDING_X_PX + CROSSHAIR_LABEL_BORDER_PX)
    );
}

type AxisPointerEvent = { axesInfo?: { axisDim: string; axisIndex: number; value: number }[] };

/** The plot pointer's time, snapped by ECharts to the nearest data point; null while hidden. */
function usePlotPointerMs(chart: ECharts | null): number | null {
    const [pointerMs, setPointerMs] = useState<number | null>(null);

    useEffect(() => {
        if (!chart) return undefined;
        const onUpdate = (event: unknown) => {
            const plotAxis = (event as AxisPointerEvent).axesInfo?.find(
                (info) => info.axisDim === "x" && info.axisIndex === 0,
            );
            setPointerMs(plotAxis ? Number(plotAxis.value) : null);
        };
        chart.on("updateAxisPointer", onUpdate);
        return () => {
            chart.off("updateAxisPointer", onUpdate);
            setPointerMs(null);
        };
    }, [chart]);

    return pointerMs;
}

/** The span pill is wide, so it is outlined rather than filled to stay light next to the time pill. */
const LIGHT_PILL_BORDER_PX = 1;
const LIGHT_PILL_STYLE: CSSProperties = {
    ...PILL_STYLE,
    border: `${LIGHT_PILL_BORDER_PX}px solid ${CROSSHAIR_LABEL_BACKGROUND}`,
    backgroundColor: "#FFFFFF",
    color: CROSSHAIR_LABEL_BACKGROUND,
};

function Pill({
    left,
    top,
    width,
    light = false,
    children,
}: {
    left: number;
    top: number;
    width: number;
    light?: boolean;
    children: ReactNode;
}) {
    return (
        <div
            className={"absolute text-center whitespace-nowrap"}
            style={{ ...(light ? LIGHT_PILL_STYLE : PILL_STYLE), left, top, width }}
        >
            {children}
        </div>
    );
}

export function BandTimePills({
    chart,
    layout,
    panelWidthPx,
    position,
    hovered,
}: {
    chart: ECharts | null;
    layout: PlotLayout;
    panelWidthPx: number;
    position: "above" | "below";
    hovered: { scheduled?: TimeEvent; actual?: TimeEvent };
}): JSX.Element | null {
    const pointerMs = usePlotPointerMs(chart);

    if (pointerMs === null || !chart) return null;

    const x = chart.convertToPixel({ xAxisIndex: 0 }, pointerMs);
    if (typeof x !== "number" || !Number.isFinite(x)) return null;

    const nowMs = Date.now();
    const parts = [
        { key: "scheduled", label: "Scheduled", span: hovered.scheduled },
        { key: "actual", label: "Valve open", span: hovered.actual },
    ].flatMap(({ key, label, span }) => (span ? [{ key, label, range: formatSpan(span, nowMs) }] : []));

    const timeText = formatAxisPointerTime(pointerMs);
    const timeWidth = pillWidth(timeText);
    const spanWidth =
        pillWidth(parts.map(({ label, range }) => `${label} ${range}`).join(SEPARATOR)) + 2 * LIGHT_PILL_BORDER_PX;
    const spanInStrip = position === "below" && parts.length > 0;
    const timeTopPx = spanInStrip
        ? X_AXIS_POINTER_LABEL_MARGIN_PX + CROSSHAIR_LABEL_HEIGHT_PX + PILL_ROW_GAP_PX
        : X_AXIS_POINTER_LABEL_MARGIN_PX;

    const spanPill = parts.length > 0 && (
        <Pill
            left={clampPillLeft(x, spanWidth, layout.gutters.left, panelWidthPx - layout.gutters.right)}
            top={position === "below" ? X_AXIS_POINTER_LABEL_MARGIN_PX : 0}
            width={spanWidth}
            light
        >
            {parts.map(({ key, label, range }, index) => (
                <span key={key}>
                    <span style={{ opacity: LABEL_OPACITY }}>
                        {index > 0 && SEPARATOR}
                        {label}{" "}
                    </span>
                    {range}
                </span>
            ))}
        </Pill>
    );

    return (
        <>
            {position === "above" && (
                <div
                    className={"pointer-events-none absolute inset-x-0"}
                    style={{ top: BAND_ABOVE_RESERVE_PX - BAND_STRIP_EXTRA_PX, height: BAND_STRIP_EXTRA_PX }}
                >
                    {spanPill}
                </div>
            )}
            <div className={"pointer-events-none absolute inset-x-0 bottom-0"} style={{ height: layout.xAxisBandPx }}>
                {position === "below" && spanPill}
                {/* Confined to the panel, as ECharts confines its own pill. */}
                <Pill left={clampPillLeft(x, timeWidth, 0, panelWidthPx)} top={timeTopPx} width={timeWidth}>
                    {timeText}
                </Pill>
            </div>
        </>
    );
}
